import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import GroupBy from 'features/gantt/menu/GroupBy';
import DisplayMenu from 'components/projectOverview/gantt/components/menus/DisplayMenu';
import SearchMenu from 'components/projectOverview/gantt/components/menus/SearchMenu';
import ZoomMenu from 'components/projectOverview/gantt/components/menus/ZoomLevelMenu';
import FilterMenu from '../../../components/projectOverview/gantt/components/menus/filters/FilterMenu';
import GanttColumnSelector from 'components/projectOverview/gantt/components/menus/GanttColumnSelector';
import {
  locationFilter,
  zoneFilter,
  paceFilter,
  tradeFilter,
  areaFilter,
  projectFilter,
  contactFilter,
  statusFilter,
  timeFrameFilter,
  jobwalkDueOpen,
  companiesFilter,
  stuckPointFilters,
  responsibleUserFilter,
} from 'components/projectOverview/gantt/components/menus/filters/filterUtils';
import ColorMenu from 'features/gantt/menu/Color';
import TodayOption from 'features/gantt/menu/Today';
import { selectAllAreas } from 'features/locations/store/areasSlice';
import { fetchLocations, selectAllLocations } from 'features/locations/store/locationsSlice';
import { selectAllZones } from 'features/locations/store/zonesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { MenuButton } from 'assets/style-components/button';

import ganttStore from 'components/projectOverview/gantt/ganttConfig/ganttStore';
import ResetIconComponent from '@blackhyve/common/src/icons/Reset';
import { resetToDefaultView, updateMenuState } from 'slices/customViewSlice';
import { Divider } from '@mui/material';
import { ActionBar } from 'components/navigation/ActionBar';
import { useGetCompanyByUserIdQuery } from 'features/companies/api/companies';
import PrintMenu from 'components/projectOverview/gantt/components/menus/PrintMenu';
import GanttSocketComponent from 'features/gantt/components/GanttSocketComponent';
import { fetchProjects, selectProjectIds } from 'features/projects/store/projectsSlice';
import { selectCurrentUserWorkspaceId } from 'features/auth';
import { fetchTasks } from 'features/tasks/store/tasksSlice';
import GanttOnlineUsers from 'features/gantt/components/GanttOnlineUsers';
import ToolsMenu from 'components/projectOverview/gantt/components/menus/ToolsMenu';

export const ContactGanttActionBar = ({ contactId, setReloadGantt, reloadGantt }) => {
  const ganttId = `contact-gantt-${contactId}`;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const projectIds = useSelector((state) => selectProjectIds(state));
  const { data: companyDetails } = useGetCompanyByUserIdQuery({ userId: contactId });
  const currentWorkspaceId = useSelector((state) => selectCurrentUserWorkspaceId(state));
  const locationGroups = useSelector((state) => selectAllLocations(state)).map((location) => {
    return { key: location.id, label: location.name };
  });
  const zoneGroups = useSelector((state) => selectAllZones(state)).map((zone) => {
    return { key: zone.id, label: zone.name };
  });
  const areaGroups = useSelector((state) => selectAllAreas(state)).map((area) => {
    return { key: area.id, label: area.name };
  });
  const colorOptions = ['Default', 'Task', 'Critical Path', 'Location', 'Zone', 'Area', 'Project'];

  const groupByOptions = {
    None: {
      name: 'None',
      key: '',
    },
    Location: {
      name: 'Location',
      key: 'location_id',
      groups: locationGroups,
      defaultGroupLabel: 'None',
    },
    Zone: {
      name: 'Zone',
      key: 'zone_id',
      groups: zoneGroups,
      defaultGroupLabel: 'None',
    },
    Area: {
      name: 'Area',
      key: 'area_id',
      groups: areaGroups,
      defaultGroupLabel: 'None',
    },
  };

  const searchByOptions = [
    {
      name: 'Name',
      value: 'text',
      compareFunction: (task, query) => {
        if (query) {
          return task.text?.toLowerCase()?.includes(query?.toLowerCase());
        }
      },
    },
  ];

  const handleResetMenu = () => {
    dispatch(resetToDefaultView());
  };

  const gantt = ganttStore.getGantt(ganttId);

  const channelNames = projectIds?.map((projectId) => {
    return `workspaces.${currentWorkspaceId}.projects.${projectId}.tasks`;
  })

  const reloadTrigger = () => {
    setReloadGantt(true);
    Promise.all([
      dispatch(fetchTasks()),
      dispatch(fetchLocations()),
      dispatch(fetchProjects()),
    ]).then(() => {
      setReloadGantt(false);
    });
  }

  useEffect(() => {
    gantt?.attachEvent('onGanttReady', function () {
      setLoading(false);
    });

    return gantt?.detachEvent('onGanttReady');
  }, [gantt]);

  useEffect(() => {
    if (companyDetails?.id) {
      dispatch(
        updateMenuState({
          name: 'filters',
          value: [
            {
              id: Math.random(),
              type: 'companiesFilter',
              value: [companyDetails],
            },
          ],
        })
      );
    }
  }, [dispatch, companyDetails]);

  return (
    <ActionBar pl={0} pr={0}>
      {loading || reloadGantt ? null : (
        <Grid container justifyContent="space-between">
          <Grid container item xs flexWrap={'nowrap'}>
            <Grid item>
              <SearchMenu ganttId={ganttId} searchByOptions={searchByOptions} />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <FilterMenu
                ganttId={ganttId}
                filterOptions={{
                  companiesFilter,
                  contactFilter,
                  responsibleUserFilter,
                  statusFilter,
                  locationFilter,
                  zoneFilter,
                  areaFilter,
                  timeFrameFilter,
                  projectFilter,
                  paceFilter,
                  tradeFilter,
                  stuckPointFilters,
                  jobwalkDueFilter: jobwalkDueOpen,
                }}
              />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <GroupBy ganttId={ganttId} options={groupByOptions} />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <GanttColumnSelector ganttId={ganttId} />
            </Grid>
            <Grid item>
              <GanttSocketComponent channelNames={channelNames} ganttId={ganttId} reloadTrigger={reloadTrigger} />
            </Grid>
          </Grid>
          <Grid
            container
            item
            flexBasis={'auto'}
            flexGrow={0}
            justifyContent={'flex-end'}
            ml={'auto'}
            sx={{ maxWidth: '100%' }}
            width={'auto'}
          >
            <Grid item>
              <TodayOption ganttId={ganttId} />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <DisplayMenu ganttId={ganttId} />
            </Grid>
            <Grid item>
              <ToolsMenu ganttId={ganttId} />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <ColorMenu ganttId={ganttId} options={colorOptions} />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <ZoomMenu ganttId={ganttId} text="Density" />
            </Grid>
            <Divider flexItem orientation="vertical" variant="middle" />
            <Grid item>
              <MenuButton onClick={handleResetMenu}>
                <span>Reset</span>
                <ResetIconComponent
                  height={16}
                  style={{ marginLeft: '5px', marginBottom: '3px' }}
                  width={16}
                />
              </MenuButton>
            </Grid>
            <Grid item>
              <PrintMenu ganttId={ganttId} printTitle={companyDetails?.name} />
            </Grid>
            <Grid item>
              <GanttOnlineUsers channelNames={channelNames} />
            </Grid>
          </Grid>
        </Grid>
      )}
    </ActionBar>
  );
};
